import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AUTO_SAVE_INTERVAL_MS } from "@/lib/constants";
import { formatFieldValue, formatFormData, formatDateIST, formatTimeIST } from "@/lib/formatUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, CheckCircle, FileEdit } from "lucide-react";
import DraftSection from "@/components/shared/DraftSection";
import ExportButtons from "@/components/shared/ExportButtons";
import EntryDetailDialog from "@/components/shared/EntryDetailDialog";
import logger from "@/lib/logger";

const COLUMNS = [
  { key: "ref_no", label: "Ref No." },
  { key: "direction", label: "Direction" },
  { key: "material_description", label: "Material" },
  { key: "quantity", label: "Qty" },
  { key: "amount_with_gst", label: "Amount With GST" },
  { key: "amount_without_gst", label: "Amount Without GST" },
  { key: "vehicle_no", label: "Vehicle No." },
  { key: "challan_no", label: "Challan / Invoice No." },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Date" },
];

export default function InterPlantTransfer() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [detailEntry, setDetailEntry] = useState(null);
  const [direction, setDirection] = useState("all");
  const [form, setForm] = useState({ direction: "to_sahnewal" });
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const autoSaveRef = useRef(null);
  const { location } = useAuth();

  const fetchEntries = useCallback(async () => {
    try {
      const params = { location, search: search || undefined };
      if (direction !== "all") params.direction = direction;
      const { data } = await api.get("/mod7/transfer", { params });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) { logger.error("Failed to fetch entries:", err); }
  }, [location, search, direction]);

  const fetchDrafts = useCallback(async () => {
    try {
      const { data } = await api.get("/drafts/transfer", { params: { location } });
      setDrafts(data.drafts || []);
    } catch (err) { logger.error("Failed to fetch drafts:", err); }
  }, [location]);

  useEffect(() => { fetchEntries(); fetchDrafts(); }, [fetchEntries, fetchDrafts]);

  // Auto-save every 30s — use refs to avoid interval recreation on every keystroke
  const formRef = useRef(form);
  formRef.current = form;
  const draftIdRef = useRef(currentDraftId);
  draftIdRef.current = currentDraftId;

  useEffect(() => {
    if (!showForm) return;
    autoSaveRef.current = setInterval(async () => {
      const f = formRef.current;
      if (!f || Object.keys(f).length === 0) return;
      try {
        const payload = { ...f, location, _draft_id: draftIdRef.current || undefined };
        const { data } = await api.post("/drafts/transfer", payload);
        if (!draftIdRef.current && data.id) setCurrentDraftId(data.id);
        setLastAutoSave(new Date());
      } catch (err) { logger.error("Auto-save failed:", err); }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(autoSaveRef.current);
  }, [showForm, location]);

  const handleSaveDraft = async () => {
    try {
      const payload = { ...form, location, _draft_id: currentDraftId || undefined };
      const { data } = await api.post("/drafts/transfer", payload);
      if (!currentDraftId && data.id) setCurrentDraftId(data.id);
      toast.success("Draft saved");
      setLastAutoSave(new Date());
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleSubmit = async () => {
    if (!form.material_description?.trim()) { toast.error("Material Description is required"); return; }
    if (!form.quantity?.toString().trim()) { toast.error("Quantity is required"); return; }
    if (!form.vehicle_no?.trim()) { toast.error("Vehicle No. is required"); return; }
    setSaving(true);
    try {
      await api.post("/mod7/transfer", { ...formatFormData(form), location });
      toast.success("Transfer entry created");
      if (currentDraftId) await api.post(`/drafts/transfer/${currentDraftId}/submit`).catch(() => {});
      closeForm();
      fetchEntries();
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const handleReceive = async (id) => {
    try {
      await api.put(`/mod7/transfer/${id}/receive`);
      toast.success("Transfer received");
      fetchEntries();
    } catch { toast.error("Failed"); }
  };

  const handleOpenDraft = async (draft) => {
    try {
      const { data } = await api.get(`/drafts/transfer/${draft.id}`);
      setForm(data.data || { direction: "to_sahnewal" });
      setCurrentDraftId(draft.id);
      setShowForm(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot open draft"); }
  };

  const handleDeleteDraft = async (draftId) => {
    try { await api.delete(`/drafts/transfer/${draftId}`); toast.success("Draft deleted"); fetchDrafts(); } catch { toast.error("Failed"); }
  };

  const closeForm = () => {
    setShowForm(false);
    if (currentDraftId) api.post(`/drafts/transfer/${currentDraftId}/release-lock`).catch(() => {});
    setCurrentDraftId(null);
    setLastAutoSave(null);
    setForm({ direction: "to_sahnewal" });
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: formatFieldValue(k, v) }));

  return (
    <div className="space-y-5" data-testid="interplant-transfer-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Inter-Plant Transfer</h1>
          <p className="text-xs sm:text-sm text-gray-500">MOD-7 · Focal Point &harr; Sahnewal wire rod coil transfers</p>
        </div>
        <Button onClick={() => { setForm({ direction: "to_sahnewal" }); setCurrentDraftId(null); setShowForm(true); }} className="bg-[#1B3C87] hover:bg-[#2650A8] h-10 sm:h-11 text-xs sm:text-sm w-full sm:w-auto" data-testid="new-entry-btn">
          <Plus size={18} className="mr-1" /> New Transfer
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex gap-2">
            {[{ v: "all", l: "All" }, { v: "to_sahnewal", l: "To Sahnewal" }, { v: "from_sahnewal", l: "From Sahnewal" }].map(d => (
              <Button
                key={d.v}
                variant={direction === d.v ? "default" : "outline"}
                className={`h-9 text-xs ${direction === d.v ? "bg-[#1B3C87]" : ""}`}
                onClick={() => setDirection(d.v)}
                data-testid={`direction-${d.v}`}
              >
                {d.l}
              </Button>
            ))}
          </div>
          <ExportButtons reportType="interplant_transfer" title="Inter-Plant Transfer" entries={entries} columns={COLUMNS} location={location} />
        </div>
        <div className="relative w-full sm:w-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-full sm:w-52" data-testid="search-input" />
        </div>
      </div>

      <DraftSection drafts={drafts} onOpen={handleOpenDraft} onDelete={handleDeleteDraft} draftType="transfer" />

      <Card>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
              {["Ref No.", "Direction", "Material", "Qty", "Amt With GST", "Amt Without GST", "Vehicle No.", "Challan / Invoice No.", "Status", "Date", "Actions"].map(h => (
                <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
            {entries.map(e => (
              <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${e.direction === "to_sahnewal" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                    {e.direction === "to_sahnewal" ? "To Sahnewal" : "From Sahnewal"}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{e.material_description || "\u2014"}</TableCell>
                <TableCell className="text-sm">{e.quantity || "\u2014"}{e.uom ? ` ${e.uom}` : ""}</TableCell>
                <TableCell className="text-sm" data-testid={`amount-with-gst-${e.id}`}>{e.amount_with_gst ? `\u20b9${e.amount_with_gst}` : "\u2014"}</TableCell>
                <TableCell className="text-sm" data-testid={`amount-without-gst-${e.id}`}>{e.amount_without_gst ? `\u20b9${e.amount_without_gst}` : "\u2014"}</TableCell>
                <TableCell className="text-sm">{e.vehicle_no || "\u2014"}</TableCell>
                <TableCell className="text-sm">{e.challan_no || "\u2014"}</TableCell>
                <TableCell><span className={`status-${e.status} px-2 py-0.5 rounded text-xs font-semibold`}>{e.status?.replace("_", " ")}</span></TableCell>
                <TableCell className="text-xs">{e.created_at ? formatDateIST(e.created_at) : "\u2014"}</TableCell>
                <TableCell>
                  {e.status === "in_transit" && (
                    <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleReceive(e.id)} data-testid={`receive-${e.id}`}>
                      <CheckCircle size={12} className="mr-1" /> Receive
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
      </Card>

      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: "Chivo" }}>
                {currentDraftId ? <span className="flex items-center gap-2"><FileEdit size={16} className="text-amber-600" /> Continue Draft</span> : "New Inter-Plant Transfer"}
              </DialogTitle>
              {lastAutoSave && <span className="text-[10px] text-gray-400">Draft saved {formatTimeIST(lastAutoSave)}</span>}
            </div>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Direction *</Label>
              <div className="grid grid-cols-2 gap-2">
                {[{ v: "to_sahnewal", l: "To Sahnewal" }, { v: "from_sahnewal", l: "From Sahnewal" }].map(d => (
                  <button
                    key={d.v}
                    type="button"
                    onClick={() => setField("direction", d.v)}
                    className={`py-3 rounded text-sm font-semibold border-2 transition-all ${
                      form.direction === d.v
                        ? "bg-[#1B3C87] text-white border-[#1B3C87]"
                        : "bg-white text-gray-600 border-gray-200"
                    }`}
                  >
                    {d.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Material Description *</Label>
              <Input value={form.material_description || ""} onChange={e => setField("material_description", e.target.value)} className="h-11" data-testid="field-material" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Quantity</Label>
              <Input type="number" value={form.quantity || ""} onChange={e => setField("quantity", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Weight (Kg)</Label>
              <Input type="number" value={form.weight || ""} onChange={e => setField("weight", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No.</Label>
              <Input value={form.vehicle_no || ""} onChange={e => setField("vehicle_no", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Coil ID / Heat No.</Label>
              <Input value={form.coil_id || ""} onChange={e => setField("coil_id", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Challan / Invoice No.</Label>
              <Input value={form.challan_no || ""} onChange={e => setField("challan_no", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Amount With GST (Rs.)</Label>
              <Input type="number" step="0.01" value={form.amount_with_gst || ""} onChange={e => setField("amount_with_gst", e.target.value)} className="h-11" placeholder="0.00" data-testid="field-amount-with-gst" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Amount Without GST (Rs.)</Label>
              <Input type="number" step="0.01" value={form.amount_without_gst || ""} onChange={e => setField("amount_without_gst", e.target.value)} className="h-11" placeholder="0.00" data-testid="field-amount-without-gst" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeForm} className="h-11">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} className="h-11 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
              <FileEdit size={14} className="mr-1" /> Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="save-entry-btn">
              {saving ? "Saving..." : "Submit Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EntryDetailDialog open={!!detailEntry} onClose={() => setDetailEntry(null)} entry={detailEntry} title="Transfer Details" collection="interplant_transfers" onRefresh={fetchEntries} />
    </div>
  );
}
